import { useFormik } from 'formik';
import moment from 'moment';
import { useEffect } from 'react';
import { IoClose } from 'react-icons/io5';
import { ActionIcon, Button, Modal, Select, Text, Textarea } from 'rizzui';
import { toast } from 'sonner';
import * as yup from 'yup';
import DateField from '../../../components/forms/DateField';
import RequiredInput from '../../../components/forms/InputHOC';
import useHandleErrors from '../../../hooks/useHandleErrors';
import { RankingService } from '../../../services/ranking/rankingService';
import { RankingFieldsModel } from '../../../type/entities/ranking-type';

interface RankingFormData {
    id: string;
    name: string;
    description: string;
    resetInterval: string;
    resetIntervalCustomStartDate: string;
    resetIntervalCustomEndDate: string;
}

interface RankingModalFormProps {
    data?: any;
    isOpen: boolean;
    onClose: () => void;
    refetch: () => void;
}

const resetIntervalOptions = [
    { label: 'Diario', value: 'daily' },
    { label: 'Semanal', value: 'weekly' },
    { label: 'Mensual', value: 'monthly' },
    { label: 'Personalizado', value: 'custom' },
];

const schema = yup.object({
    name: yup.string().min(1, 'Demasiado corto').max(300, 'Demasiado largo').required('Campo obligatorio'),
    description: yup.string().nullable(),
    resetInterval: yup.string().required('Campo obligatorio'),
    resetIntervalCustomStartDate: yup.string().when('resetInterval', {
        is: 'custom',
        then: (schema) => schema.required('La fecha de inicio es requerida para intervalos personalizados'),
        otherwise: (schema) => schema.nullable(),
    }),
    resetIntervalCustomEndDate: yup.string().when('resetInterval', {
        is: 'custom',
        then: (schema) => schema.required('La fecha de fin es requerida para intervalos personalizados')
            .test('is-after-start', 'La fecha de fin debe ser posterior a la fecha de inicio', function (value) {
                const { resetIntervalCustomStartDate } = this.parent;
                if (!resetIntervalCustomStartDate || !value) return true;
                return new Date(value) > new Date(resetIntervalCustomStartDate);
            }),
        otherwise: (schema) => schema.nullable(),
    }),
});

const RankingModalForm: React.FC<RankingModalFormProps> = ({ data, isOpen, onClose, refetch }) => {

    const { handleErrors } = useHandleErrors();
    const rankingService = new RankingService();

    const handleClose = () => {
        formik.resetForm();
        onClose();
    };

    const create = async (values: any) => {
        const payload: RankingFieldsModel = {
            rankingId: null,
            name: values.name,
            description: values.description || '',
            resetInterval: values.resetInterval,
            resetIntervalCustomStartDate: values.resetInterval === 'custom' ? values.resetIntervalCustomStartDate : null,
            resetIntervalCustomEndDate: values.resetInterval === 'custom' ? values.resetIntervalCustomEndDate : null,
        };
        try {
            const response = await (await rankingService.createRanking(payload)).getResponseData();
            if (response.success) {
                toast.success('Ranking creado correctamente');
                formik.resetForm();
                refetch();
                onClose();
            } else {
                handleErrors(response);
            }
        } catch (error: any) {
            toast.error('Error al crear el ranking');
        } finally {
            handleClose();
        }
    };

    const edit = async (values: any) => {
        const payload: RankingFieldsModel = {
            id: data?.id || null,
            name: values.name,
            description: values.description || '',
            resetInterval: values.resetInterval,
            // Asignar fechas personalizadas solo si el intervalo es 'custom'
            resetIntervalCustomStartDate: values.resetInterval === 'custom' ? values.resetIntervalCustomStartDate : null,
            resetIntervalCustomEndDate: values.resetInterval === 'custom' ? values.resetIntervalCustomEndDate : null,
        };
        try {
            const response = await (await (new RankingService()).editRanking(payload)).getResponseData();
            if (response.success) {
                toast.success('Ranking actualizado correctamente');
                refetch();
            } else {
                handleErrors(response);
            }
        } catch (error: any) {
            toast.error('Error al actualizar el ranking');
        } finally {
            handleClose();
        }
    };

    const formik = useFormik({
        initialValues: {
            id: data?.id || '',
            name: data?.name || '',
            description: data?.description || '',
            resetInterval: data?.resetInterval || 'daily',
            resetIntervalCustomStartDate: data?.resetIntervalCustomStartDate || moment().subtract(2, 'months').format('YYYY-MM-DD'),
            resetIntervalCustomEndDate: data?.resetIntervalCustomEndDate || moment().format('YYYY-MM-DD'),
        },
        validationSchema: schema,
        validateOnBlur: false,
        onSubmit: (values: RankingFormData) => { data?.id ? edit(values) : create(values) },
    });

    useEffect(() => {
        if (data && data.id) {
            formik.setValues({
                id: data.id,
                name: data.name,
                description: data.description || '',
                resetInterval: data.resetInterval || 'daily',
                resetIntervalCustomStartDate: data.resetIntervalCustomStartDate || moment().subtract(2, 'months').format('YYYY-MM-DD'),
                resetIntervalCustomEndDate: data.resetIntervalCustomEndDate || moment().format('YYYY-MM-DD'),
            });
        }
    }, [data]);

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            size='md'
            overlayClassName='backdrop-blur'
            containerClassName='!max-w-2xl !shadow-xl'
            className='z-[999] [&_.pointer-events-none]:overflow-visible'
        >
            <form onSubmit={formik.handleSubmit}>
                <div className='m-auto px-7 pt-6 pb-4'>
                    <div className='mb-7 flex items-center justify-between flex-shrink-0'>
                        <Text className="text-xl">{data?.id ? 'Editar' : 'Crear'} Ranking</Text>
                        <ActionIcon size='sm' variant='text' onClick={handleClose}>
                            <IoClose className='h-auto w-6' strokeWidth={1.8} />
                        </ActionIcon>
                    </div>

                    <div className='grid grid-cols-1 gap-4'>
                        <RequiredInput
                            id='name'
                            name='name'
                            label='Nombre'
                            value={formik.values.name}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.name && formik.errors.name ? String(formik.errors.name) : ''}
                        />

                        <Textarea
                            id='description'
                            name='description'
                            label='Descripción'
                            value={formik.values.description}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            rows={3}
                            error={formik.touched.description && formik.errors.description ? String(formik.errors.description) : ''}
                        />

                        <Select
                            label='Intervalo de reinicio'
                            options={resetIntervalOptions}
                            value={{ label: resetIntervalOptions.find(option => option.value === formik.values.resetInterval)?.label || 'Seleccione un intervalo', value: formik.values.resetInterval }}
                            onChange={(e: any) => {
                                formik.setFieldValue('resetInterval', e?.value);
                            }}
                            placeholder='Seleccione un intervalo'
                            error={formik.touched.resetInterval && formik.errors.resetInterval ? String(formik.errors.resetInterval) : ''}
                        />

                        {formik.values.resetInterval === 'custom' && (
                            <>
                                <DateField
                                    id='resetIntervalCustomDate'
                                    name='resetIntervalCustomDate'
                                    label={<span>Fecha de inicio y fin <span className='text-red-500'>*</span></span>}
                                    startDate={new Date(formik.values.resetIntervalCustomStartDate as string) || null}
                                    endDate={new Date(formik.values.resetIntervalCustomEndDate as string) || null}
                                    onChange={(value: string) => {
                                        formik.setFieldValue('resetIntervalCustomStartDate', value[0]);
                                        formik.setFieldValue('resetIntervalCustomEndDate', value[1]);
                                    }}
                                    onBlur={formik.handleBlur}
                                    placeholder='Seleccione fecha de inicio y fin'
                                    className='w-full'
                                />
                                {formik.touched.resetIntervalCustomStartDate && formik.errors.resetIntervalCustomStartDate || formik.touched.resetIntervalCustomEndDate && formik.errors.resetIntervalCustomEndDate ? (
                                    <p className='mt-1 text-sm text-red-600'>{String(formik.errors.resetIntervalCustomStartDate || formik.errors.resetIntervalCustomEndDate)}</p>
                                ) : null}
                            </>
                        )}
                    </div>

                    <div className='mt-8 flex justify-end'>
                        <Button
                            type='submit'
                            isLoading={formik.isSubmitting}
                            disabled={formik.isSubmitting}
                        >
                            {data?.id ? 'Actualizar' : 'Crear'} Ranking
                        </Button>
                    </div>
                </div>
            </form>
        </Modal>
    );
};

export default RankingModalForm;